import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/brand";

const SectionLabel = ({ children }) => <span className="haven-label">{children}</span>;

const fmt = (n) =>
  n >= 1000
    ? `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
    : `$${Number(n).toFixed(0)}`;

const NumberField = ({ id, label, prefix, suffix, min, max, step = 1, value, onChange, helper, dataTestId }) => (
  <label htmlFor={id} className="block">
    <span style={{ fontSize: 12, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--ink-muted)" }}>
      {label}
    </span>
    <div className="haven-card mt-2" style={{ padding: "10px 14px", display: "flex", alignItems: "center", gap: 8 }}>
      {prefix && <span style={{ color: "var(--ink-soft)", fontSize: 14 }}>{prefix}</span>}
      <input
        id={id}
        data-testid={dataTestId}
        type="number"
        inputMode="decimal"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          flex: 1,
          background: "transparent",
          border: "none",
          outline: "none",
          fontFamily: "Cormorant Garamond, Georgia, serif",
          fontSize: 28,
          color: "var(--ink)",
          padding: 0,
        }}
      />
      {suffix && <span style={{ color: "var(--ink-muted)", fontSize: 12 }}>{suffix}</span>}
    </div>
    {helper && (
      <span style={{ display: "block", marginTop: 6, fontSize: 11, color: "var(--ink-muted)", lineHeight: 1.5 }}>
        {helper}
      </span>
    )}
  </label>
);

export default function EmbroideryCost() {
  const [ruined, setRuined] = useState("3");
  const [costPerBlank, setCostPerBlank] = useState("28");
  const [redoHours, setRedoHours] = useState("2");
  const [hourly, setHourly] = useState("35");
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | done | error
  const [errMsg, setErrMsg] = useState("");
  const [result, setResult] = useState(null);

  // Live preview math (frontend mirrors backend; backend remains source of truth)
  const preview = useMemo(() => {
    const r = parseFloat(ruined) || 0;
    const c = parseFloat(costPerBlank) || 0;
    const h = parseFloat(redoHours) || 0;
    const hr = parseFloat(hourly) || 0;
    const materials = r * c * 12;
    const time = h * hr * 52;
    const annual = materials + time;
    return { materials, time, annual, monthly: annual / 12 };
  }, [ruined, costPerBlank, redoHours, hourly]);

  const submit = async (e) => {
    e.preventDefault();
    if (state === "loading") return;
    if (!email || !email.includes("@")) {
      setErrMsg("We need an email to send your full report.");
      setState("error");
      return;
    }
    setState("loading");
    setErrMsg("");
    try {
      const { data } = await api.post("/calculate/embroidery-loss", {
        email,
        ruined_blanks_per_month: parseFloat(ruined) || 0,
        avg_cost_per_blank: parseFloat(costPerBlank) || 0,
        redo_hours_per_week: parseFloat(redoHours) || 0,
        hourly_value: parseFloat(hourly) || 0,
      });
      setResult(data);
      setState("done");
    } catch (err) {
      setErrMsg(err?.response?.data?.detail || "The calculator hit a snag. Try again with whole numbers.");
      setState("error");
    }
  };

  const reset = () => {
    setResult(null);
    setState("idle");
  };

  return (
    <main data-testid="embroidery-cost-page" className="haven-page-shell" style={{ paddingTop: 32 }}>
      <SectionLabel>Free · 4 numbers · Daniel's framework</SectionLabel>
      <h1
        className="font-serif mt-3"
        style={{
          fontSize: "clamp(36px, 5vw, 56px)",
          lineHeight: 1.05,
          color: "var(--ink)",
          letterSpacing: "-0.01em",
        }}
      >
        What is your machine
        <br />
        <em style={{ color: "var(--gold)" }}>actually</em> costing you?
      </h1>
      <p className="mt-4" style={{ color: "var(--ink-soft)", fontSize: 15, lineHeight: 1.7, maxWidth: 640 }}>
        Embroiderers eat hidden losses every month — ruined blanks, redo time, second-guessing placement. Tell us four
        numbers. We'll show you the annual figure most embroiderers never sit down and add up.
      </p>

      {!result && (
        <div className="grid lg:grid-cols-12 gap-6 mt-8">
          {/* Inputs */}
          <form onSubmit={submit} data-testid="embroidery-form" className="lg:col-span-7 space-y-5">
            <NumberField
              id="ec-ruined"
              dataTestId="ec-ruined-input"
              label="Ruined or scrapped blanks per month"
              suffix="blanks/mo"
              min={0}
              max={200}
              value={ruined}
              onChange={setRuined}
              helper="Be honest. Even 1 a month adds up — and most embroiderers undercount because they reuse memory of recent good months."
            />
            <NumberField
              id="ec-cost"
              dataTestId="ec-cost-input"
              label="Average cost per blank"
              prefix="$"
              min={0}
              max={2000}
              value={costPerBlank}
              onChange={setCostPerBlank}
              helper="Materials only — shirt, jacket, towel, hat — plus stabilizer & thread you can't recover."
            />
            <NumberField
              id="ec-hours"
              dataTestId="ec-hours-input"
              label="Redo / re-hoop hours per week"
              suffix="hrs/wk"
              min={0}
              max={80}
              step={0.5}
              value={redoHours}
              onChange={setRedoHours}
              helper="Hooping a second time. Picking out stitches. Re-running a design. Setup that should have been one shot."
            />
            <NumberField
              id="ec-hourly"
              dataTestId="ec-hourly-input"
              label="What is your time worth per hour?"
              prefix="$"
              suffix="/hr"
              min={0}
              max={500}
              value={hourly}
              onChange={setHourly}
              helper="Use what you'd charge a customer, or what you'd pay an assistant. Don't put $0 — your time isn't free."
            />

            <div className="haven-card haven-card-gold" style={{ padding: 22, marginTop: 8 }}>
              <SectionLabel>Almost there</SectionLabel>
              <h3 className="font-serif mt-2" style={{ fontSize: 22, color: "var(--ink)", lineHeight: 1.2 }}>
                Where should we send your full breakdown?
              </h3>
              <p style={{ marginTop: 6, color: "var(--ink-soft)", fontSize: 12.5, lineHeight: 1.55 }}>
                We email the result + the 5-email gap primer. No spam. Unsubscribe in one click.
              </p>
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                <input
                  data-testid="ec-email-input"
                  className="haven-input"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <button
                  data-testid="ec-submit"
                  type="submit"
                  className="haven-btn haven-btn-primary"
                  disabled={state === "loading"}
                >
                  {state === "loading" ? "Calculating…" : "Show me the number"}
                </button>
              </div>
              {state === "error" && (
                <div data-testid="ec-error" style={{ color: "var(--rose)", fontSize: 12, marginTop: 8 }}>
                  {errMsg}
                </div>
              )}
            </div>
          </form>

          {/* Live preview panel */}
          <aside className="lg:col-span-5">
            <div
              className="haven-card"
              style={{
                padding: 24,
                position: "sticky",
                top: 96,
                background: "var(--ink)",
                color: "var(--warm-white)",
                borderColor: "var(--ink)",
              }}
              data-testid="ec-preview"
            >
              <span className="haven-label" style={{ color: "var(--gold-light)" }}>Live preview</span>
              <div className="font-serif mt-3" style={{ fontSize: 56, lineHeight: 1, color: "var(--gold-light)" }}>
                {fmt(preview.annual)}
              </div>
              <div style={{ fontSize: 12, color: "#D5C9B5", marginTop: 4 }}>
                estimated annual loss · {fmt(preview.monthly)} every month
              </div>
              <div className="haven-divider-grain" style={{ margin: "20px 0", background: "rgba(251,248,242,0.18)" }} />
              <div className="grid grid-cols-2 gap-4" style={{ fontSize: 12 }}>
                <div>
                  <div style={{ color: "var(--gold-light)", letterSpacing: "0.08em", textTransform: "uppercase", fontSize: 10 }}>Materials</div>
                  <div className="font-serif mt-1" style={{ fontSize: 22 }}>{fmt(preview.materials)}</div>
                  <div style={{ fontSize: 10, color: "#A89A85" }}>per year</div>
                </div>
                <div>
                  <div style={{ color: "var(--gold-light)", letterSpacing: "0.08em", textTransform: "uppercase", fontSize: 10 }}>Time value</div>
                  <div className="font-serif mt-1" style={{ fontSize: 22 }}>{fmt(preview.time)}</div>
                  <div style={{ fontSize: 10, color: "#A89A85" }}>per year</div>
                </div>
              </div>
              <p className="mt-5" style={{ fontSize: 12, color: "#C7BBA6", lineHeight: 1.55 }}>
                Numbers update as you type. Submit to get Daniel's full verdict and the comparison to the founding rate.
              </p>
            </div>
          </aside>
        </div>
      )}

      {/* Result */}
      {result && (
        <div data-testid="ec-result" className="mt-8 grid gap-4 max-w-3xl">
          <div className="haven-card" style={{ padding: 30, background: "var(--ink)", color: "var(--warm-white)", borderColor: "var(--ink)" }}>
            <SectionLabel>Your verdict</SectionLabel>
            <div className="font-serif mt-3" style={{ fontSize: "clamp(56px, 8vw, 88px)", lineHeight: 1, color: "var(--gold-light)" }}>
              {fmt(result.annual_loss_usd)}
            </div>
            <div style={{ marginTop: 6, color: "#D5C9B5", fontSize: 13, letterSpacing: "0.04em" }}>
              estimated annual loss · {fmt(result.monthly_loss_usd)} every month
            </div>
            <p className="font-serif italic" style={{ marginTop: 18, fontSize: 22, lineHeight: 1.4, color: "var(--warm-white)" }}>
              "{result.headline_verdict}"
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="haven-card haven-card-gold" style={{ padding: 22 }}>
              <SectionLabel>Lifetime math</SectionLabel>
              <p style={{ marginTop: 8, color: "var(--ink)", fontSize: 14.5, lineHeight: 1.6 }}>
                {result.comparison_to_lifetime}
              </p>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-serif" style={{ fontSize: 32, color: "var(--ink)", lineHeight: 1 }}>
                  {result.months_to_pay_back_lifetime < 999 ? `${result.months_to_pay_back_lifetime}` : "—"}
                </span>
                <span style={{ color: "var(--ink-muted)", fontSize: 12 }}>months to pay itself back</span>
              </div>
            </div>
            <div className="haven-card" style={{ padding: 22 }}>
              <SectionLabel>Monthly math</SectionLabel>
              <p style={{ marginTop: 8, color: "var(--ink)", fontSize: 14.5, lineHeight: 1.6 }}>
                {result.comparison_to_annual}
              </p>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-serif" style={{ fontSize: 32, color: "var(--gold)", lineHeight: 1 }}>$29</span>
                <span style={{ color: "var(--ink-muted)", fontSize: 12 }}>/mo · founding rate locked permanently</span>
              </div>
            </div>
          </div>

          <div className="haven-card" style={{ padding: 22 }}>
            <SectionLabel>Daniel's note</SectionLabel>
            <p className="font-serif italic mt-2" style={{ color: "var(--ink)", fontSize: 18, lineHeight: 1.5 }}>
              "{result.daniel_note}"
            </p>
            <div style={{ borderTop: "1px solid var(--border-soft)", marginTop: 16, paddingTop: 16, fontSize: 12, color: "var(--ink-muted)", lineHeight: 1.55 }}>
              Materials lost per year: <b style={{ color: "var(--ink)" }}>{fmt(result.materials_loss_annual)}</b> · Time value lost: <b style={{ color: "var(--ink)" }}>{fmt(result.time_loss_annual)}</b>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Link to="/join" data-testid="ec-cta-join" className="haven-btn haven-btn-gold">
              Lock founding rate · stop the bleed
            </Link>
            <button onClick={reset} data-testid="ec-reset" className="haven-btn haven-btn-ghost">
              Recalculate
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
